import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { FaTrophy, FaCertificate, FaAward, FaStar, FaAws, FaCoins, FaLaptopCode, FaRocket } from 'react-icons/fa';

// Custom Count Up component using requestAnimationFrame
const CountUp = ({ to, duration = 1.0 }) => {
  const [count, setCount] = useState('0');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  
  useEffect(() => {
    if (!isInView) return;
    
    const numberPart = parseInt(to.replace(/[^0-9]/g, ''), 10) || 0;
    const suffixPart = to.replace(/[0-9]/g, '');
    
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
      const currentVal = Math.floor(progress * numberPart);
      
      setCount(`${currentVal}${suffixPart}`);
      
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    
    window.requestAnimationFrame(step);
  }, [isInView, to, duration]);

  return <span ref={ref} className="number-readout">{count}</span>;
};

const Achievements = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const controls = useAnimation();
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);

  const quotes = [
    "Every achievement is a stepping stone towards building impactful AI solutions that make a real difference in the world.",
    "I can do all things through Christ who strengthens me. - Philippians 4:13",
    "For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future. - Jeremiah 29:11",
    "Commit to the Lord whatever you do, and He will establish your plans. - Proverbs 16:3",
    "Whatever you do, work at it with all your heart, as working for the Lord. - Colossians 3:23",
    "Trust in the Lord with all your heart and lean not on your own understanding. - Proverbs 3:5",
    "Innovation distinguishes between a leader and a follower. Strive for excellence in all you create.",
    "The fear of the Lord is the beginning of wisdom, and knowledge of the Holy One is understanding. - Proverbs 9:10",
    "Success is not final, failure is not fatal: it is the courage to continue that counts in the journey of innovation.",
    "Be strong and courageous. Do not be afraid; do not be discouraged, for the Lord your God will be with you wherever you go. - Joshua 1:9"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuoteIndex((prevIndex) => (prevIndex + 1) % quotes.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [quotes.length]);

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  const achievements = [
    {
      title: 'Top 1 - Bi3 Bytes Hackathon 🏆',
      subtitle: '₹20,000 Cash Prize',
      description: 'First place winner with innovative AI computer vision platform demonstrating technical excellence.',
      icon: FaTrophy,
      year: '2025',
      badgeText: 'WIN_01',
      featured: true,
    },
    {
      title: 'Top 2 - Code O Clock Hackathon',
      subtitle: '₹15,000 Cash Prize',
      description: 'Second place winner showcasing advanced object detection and real-time inference systems.',
      icon: FaTrophy,
      year: '2025',
      badgeText: 'RANK_02',
    },
    {
      title: 'Top 9 - Google x GeeksforGeeks',
      subtitle: 'National Level Hackathon',
      description: 'Ranked in top 9 among thousands of developers in prestigious national coding contest.',
      icon: FaAward,
      year: '2025',
      badgeText: 'RANK_09',
    },
    {
      title: 'Best Project - Dev Arena',
      subtitle: 'SRM University Chennai',
      description: 'Awarded Best Project for exceptional computer vision innovation and technical execution.',
      icon: FaStar,
      year: '2025',
      badgeText: 'BEST_PROJECT',
    },
    {
      title: 'Intel Unnati Industrial Training',
      subtitle: 'AI & Web Development',
      description: 'Completed intensive industrial training on deep learning modeling and web-app routing.',
      icon: FaCertificate,
      year: '2025',
      badgeText: 'TRAINING',
    },
    {
      title: 'AWS Academy Graduate',
      subtitle: 'Cloud Computing Architecture',
      description: 'Certified in Amazon Web Services cloud architecture and scalable server deployment.',
      icon: FaAws,
      year: '2024',
      badgeText: 'CLOUDMAP_01',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const cardVariants = {
    hidden: { scale: 0.98, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const renderBrackets = () => (
    <>
      <span className="reticle-bracket top-0 left-0 border-t border-l border-white-pure group-hover:translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket top-0 right-0 border-t border-r border-white-pure group-hover:-translate-x-0.5 group-hover:translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 left-0 border-b border-l border-white-pure group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
      <span className="reticle-bracket bottom-0 right-0 border-b border-r border-white-pure group-hover:-translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
    </>
  );

  return (
    <section
      id="achievements"
      ref={sectionRef}
      className="section-padding relative overflow-hidden bg-void border-t border-grey-800"
    >
      <div className="container-custom relative z-10">
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="mb-20 text-left border-l-2 border-white-primary pl-6"
        >
          <motion.span
            variants={cardVariants}
            className="inline-block text-[9px] font-mono font-bold tracking-[0.35em] uppercase text-grey-400 mb-2"
          >
            05 // SYSTEM_RECOGNITION
          </motion.span>
          <motion.h2
            variants={cardVariants}
            className="text-3xl sm:text-5xl md:text-6xl font-display font-black"
          >
            ACHIEVEMENTS // <span className="chrome-text">LOGS</span>
          </motion.h2>
        </motion.div>

        {/* Achievements Cards Grid */}
        <motion.div 
          className="achievements-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {achievements.map((achievement, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className={`achievement-card group ${achievement.featured ? 'md:col-span-2 lg:col-span-1' : ''}`}
            >
              <div
                className={`hud-card h-full p-6 relative overflow-hidden text-left flex flex-col justify-between ${
                  achievement.featured ? 'border-grey-600' : 'border-grey-800'
                }`}
              >
                {renderBrackets()}

                {/* Featured Badge */}
                {achievement.featured && (
                  <div className="absolute top-4 right-4 px-2 py-0.5 bg-white-primary text-[8px] font-mono font-bold text-void uppercase tracking-widest">
                    FEATURED
                  </div>
                )}

                <div>
                  {/* Icon */}
                  <div className="w-10 h-10 bg-grey-800 flex items-center justify-center mb-5 border border-grey-600">
                    <achievement.icon className="text-sm text-white-pure" />
                  </div>

                  {/* Year & Tech Badge */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-void border border-grey-800 text-[8px] font-mono font-bold tracking-widest text-grey-400">
                      YEAR // {achievement.year}
                    </div>
                    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-void border border-grey-800 text-[8px] font-mono font-bold tracking-widest text-white-pure">
                      ID // {achievement.badgeText}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-display font-bold mb-1 group-hover:text-white-pure transition-colors">
                    {achievement.title}
                  </h3>
                  
                  <p className="text-[10px] font-mono text-grey-400 tracking-wider uppercase mb-3">
                    {achievement.subtitle}
                  </p>
                </div>
                
                <p className="text-grey-400 text-xs leading-relaxed font-sans mt-2">
                  {achievement.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Counter Stats Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="bg-elevated border border-grey-800 p-8 lg:p-12"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '4+', label: 'HACKATHON_WINS', icon: FaTrophy },
              { value: '55K+', label: 'PRIZE_MONEY_INR', icon: FaCoins },
              { value: '10+', label: 'SYSTEMS_DEPLOYED', icon: FaLaptopCode },
              { value: '100%', label: 'DEDICATION_SCORE', icon: FaRocket },
            ].map((stat, index) => {
              const StatIcon = stat.icon;
              return (
                <div
                  key={index}
                  className="text-center group cursor-pointer flex flex-col items-center justify-center"
                >
                  <div className="w-8 h-8 bg-void border border-grey-800 flex items-center justify-center mb-3">
                    <StatIcon className="text-xs text-grey-400 group-hover:text-white-pure transition-colors" />
                  </div>
                  <div className="text-3xl lg:text-4xl font-mono font-bold text-white-pure mb-2">
                    <CountUp to={stat.value} />
                  </div>
                  <div className="text-grey-400 text-[8px] font-mono uppercase tracking-widest">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Rotating quotes slider */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate={controls}
          className="mt-16 text-center"
        >
          <div className="bg-elevated border border-grey-800 p-8 max-w-2xl mx-auto relative overflow-hidden">
            <div className="text-4xl font-mono text-grey-800/20 absolute top-4 left-6 pointer-events-none select-none">“</div>
            
            <motion.p
              key={currentQuoteIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="text-sm italic text-grey-200 leading-relaxed min-h-[60px] flex items-center justify-center font-mono px-6 uppercase"
            >
              {quotes[currentQuoteIndex]}
            </motion.p>
            
            <div className="text-4xl font-mono text-grey-800/20 absolute bottom-4 right-6 rotate-180 pointer-events-none select-none">“</div>
            
            {/* Slide dots */}
            <div className="flex justify-center gap-1.5 mt-6">
              {quotes.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentQuoteIndex(index)}
                  className={`w-2.5 h-1 border transition-all duration-300 ${
                    index === currentQuoteIndex
                      ? 'bg-white-pure border-white-pure w-6'
                      : 'bg-grey-800 border-transparent hover:bg-grey-600'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Achievements;
